/**
 * Network Compatibility Utility Functions
 * Check if cryptocurrencies can be transferred between exchanges
 */

import type { NetworkStatusData, Crypto } from '../types/networkStatus';
import type { ArbitrageOpportunity } from '../types/opportunity';

/**
 * Network name normalization map
 * Some exchanges use different names for the same network
 */
const NETWORK_ALIASES: { [key: string]: string } = {
  // Binance Smart Chain
  'BSC': 'BEP20',
  'BEP20': 'BEP20',
  'BNB Smart Chain': 'BEP20',
  'BNB': 'BEP20',
  
  // Ethereum
  'Ethereum': 'ERC20',
  'ERC20': 'ERC20',
  'ETH': 'ERC20',
  
  // Tron
  'TRON': 'TRC20',
  'TRC20': 'TRC20',
  'TRX': 'TRC20',
  
  // Polygon/Matic
  'Polygon': 'MATIC',
  'MATIC': 'MATIC',
  'POLYGON': 'MATIC',
  
  // Avalanche C-Chain
  'Avalanche C-Chain': 'AVAXC',
  'AVAXC': 'AVAXC',
  'AVAX C-Chain': 'AVAXC',
  'Avalanche': 'AVAXC',
  
  // Solana
  'Solana': 'SOL',
  'SOL': 'SOL',
  
  // Arbitrum
  'Arbitrum One': 'ARBITRUM',
  'Arbitrum': 'ARBITRUM',
  'ARB': 'ARBITRUM',
  
  // Optimism
  'Optimism': 'OPTIMISM',
  'OP': 'OPTIMISM',
  
  // Base
  'Base': 'BASE',
  'BASE': 'BASE',
};

/**
 * Normalize network name to handle variations across exchanges
 */
function normalizeNetworkName(network: string): string {
  const upper = network.toUpperCase();
  return NETWORK_ALIASES[upper] || upper;
}

/**
 * Check if two networks are the same (accounting for name variations)
 */
export function isSameNetwork(network1: string, network2: string): boolean {
  return normalizeNetworkName(network1) === normalizeNetworkName(network2);
}

/**
 * Find common networks between two crypto assets
 * Returns networks that support withdrawal from source and deposit to destination
 */
function findCommonNetworks(
  sourceCrypto: Crypto | undefined,
  destinationCrypto: Crypto | undefined
): string[] {
  if (!sourceCrypto || !destinationCrypto) {
    return [];
  }

  const commonNetworks: string[] = [];

  // Check each source network
  for (const sourceNetwork of sourceCrypto.networks) {
    // Skip if withdrawal is not enabled on source
    if (!sourceNetwork.withdraw_enabled) {
      continue;
    }

    // Find matching network on destination
    for (const destNetwork of destinationCrypto.networks) {
      // Check if deposit is enabled and networks match
      if (destNetwork.deposit_enabled && 
          isSameNetwork(sourceNetwork.network, destNetwork.network)) {
        commonNetworks.push(sourceNetwork.network);
        break; // Found match, move to next source network
      }
    }
  }

  return commonNetworks;
}

/**
 * Check if a cryptocurrency can be transferred between two exchanges
 */
export function isTransferrable(
  symbol: string,
  buyExchange: number,
  sellExchange: number,
  networkStatus: NetworkStatusData
): boolean {
  // Get network status for both exchanges
  const buyStatus = networkStatus[buyExchange];
  const sellStatus = networkStatus[sellExchange];

  // If either exchange has no data, assume transferrable (fail open)
  if (!buyStatus || !sellStatus) {
    return true;
  }

  // Find the crypto on both exchanges - check if cryptos is an array first
  const buyCrypto = Array.isArray(buyStatus.cryptos)
    ? buyStatus.cryptos.find(c => c.symbol === symbol)
    : undefined;
  const sellCrypto = Array.isArray(sellStatus.cryptos)
    ? sellStatus.cryptos.find(c => c.symbol === symbol)
    : undefined;

  // If crypto not found on either exchange, assume not transferrable
  if (!buyCrypto || !sellCrypto) {
    console.log(`${symbol} not found on exchange ${buyExchange} or ${sellExchange}`);
    return false;
  }

  // Find common networks
  const commonNetworks = findCommonNetworks(buyCrypto, sellCrypto);
  
  if (commonNetworks.length === 0) {
    console.log(`No common networks for ${symbol} between exchanges ${buyExchange} and ${sellExchange}`);
    return false;
  }

  console.log(`Found ${commonNetworks.length} common network(s) for ${symbol} between exchanges ${buyExchange} and ${sellExchange}: ${commonNetworks.join(', ')}`);
  return true;
}

/**
 * Check if an arbitrage opportunity is viable for transfer
 */
export function isOpportunityTransferrable(
  opportunity: ArbitrageOpportunity,
  networkStatus: NetworkStatusData
): boolean {
  // Use the base currency directly from the opportunity
  const baseCurrency = opportunity.base;

  return isTransferrable(
    baseCurrency,
    opportunity.buy_exchange,
    opportunity.sell_exchange,
    networkStatus
  );
}

/**
 * Get transfer fee for a specific network
 */
export function getWithdrawalFee(
  symbol: string,
  exchange: number,
  network: string,
  networkStatus: NetworkStatusData
): number | undefined {
  const exchangeStatus = networkStatus[exchange];
  if (!exchangeStatus) return undefined;

  const crypto = Array.isArray(exchangeStatus.cryptos)
    ? exchangeStatus.cryptos.find(c => c.symbol === symbol)
    : undefined;
  if (!crypto) return undefined;

  const networkInfo = crypto.networks.find(n => 
    isSameNetwork(n.network, network)
  );
  
  return networkInfo?.withdraw_fee;
}

/**
 * Get minimum withdrawal amount for a specific network
 */
export function getMinWithdrawal(
  symbol: string,
  exchange: number,
  network: string,
  networkStatus: NetworkStatusData
): number | undefined {
  const exchangeStatus = networkStatus[exchange];
  if (!exchangeStatus) return undefined;

  const crypto = Array.isArray(exchangeStatus.cryptos)
    ? exchangeStatus.cryptos.find(c => c.symbol === symbol)
    : undefined;
  if (!crypto) return undefined;

  const networkInfo = crypto.networks.find(n => 
    isSameNetwork(n.network, network)
  );
  
  return networkInfo?.min_withdraw;
}

/**
 * Get minimum deposit amount for a specific network
 */
export function getMinDeposit(
  symbol: string,
  exchange: number,
  network: string,
  networkStatus: NetworkStatusData
): number | undefined {
  const exchangeStatus = networkStatus[exchange];
  if (!exchangeStatus) return undefined;

  const crypto = Array.isArray(exchangeStatus.cryptos)
    ? exchangeStatus.cryptos.find(c => c.symbol === symbol)
    : undefined;
  if (!crypto) return undefined;

  const networkInfo = crypto.networks.find(n => 
    isSameNetwork(n.network, network)
  );
  
  return networkInfo?.min_deposit;
}

/**
 * Find the cheapest network for transfer between two exchanges
 */
export function findCheapestNetwork(
  symbol: string,
  buyExchange: number,
  sellExchange: number,
  networkStatus: NetworkStatusData
): { network: string; fee: number } | null {
  const buyStatus = networkStatus[buyExchange];
  const sellStatus = networkStatus[sellExchange];

  if (!buyStatus || !sellStatus) return null;

  const buyCrypto = Array.isArray(buyStatus.cryptos)
    ? buyStatus.cryptos.find(c => c.symbol === symbol)
    : undefined;
  const sellCrypto = Array.isArray(sellStatus.cryptos)
    ? sellStatus.cryptos.find(c => c.symbol === symbol)
    : undefined;

  if (!buyCrypto || !sellCrypto) return null;

  const commonNetworks = findCommonNetworks(buyCrypto, sellCrypto);
  if (commonNetworks.length === 0) return null;

  let cheapestNetwork: { network: string; fee: number } | null = null;

  for (const network of commonNetworks) {
    const sourceNetwork = buyCrypto.networks.find(n => 
      isSameNetwork(n.network, network)
    );
    
    if (sourceNetwork?.withdraw_fee !== undefined) {
      if (!cheapestNetwork || sourceNetwork.withdraw_fee < cheapestNetwork.fee) {
        cheapestNetwork = {
          network: sourceNetwork.network,
          fee: sourceNetwork.withdraw_fee,
        };
      }
    }
  }

  return cheapestNetwork;
}

/**
 * Check if network status data is available and not empty
 */
export function hasNetworkStatusData(networkStatus: NetworkStatusData): boolean {
  // Check if array has any non-undefined elements
  return Array.isArray(networkStatus) && networkStatus.some(data => data !== undefined);
}

/**
 * Default behavior when network status is unavailable
 * Returns true to allow all opportunities (fail open)
 */
export function defaultTransferrableStatus(): boolean {
  return true;
}

/**
 * Transfer status for visual indicators
 */
export type TransferStatus = 'enabled' | 'disabled' | 'unknown';

/**
 * Check withdrawal status for a specific cryptocurrency on an exchange
 */
export function getWithdrawStatus(
  symbol: string,
  exchange: number,
  networkStatus: NetworkStatusData
): TransferStatus {
  const exchangeStatus = networkStatus[exchange];
  if (!exchangeStatus) return 'unknown';

  // Check if cryptos is an array
  if (!Array.isArray(exchangeStatus.cryptos)) {
    console.warn(`Exchange ${exchange} cryptos is not an array:`, exchangeStatus.cryptos);
    return 'unknown';
  }

  const crypto = exchangeStatus.cryptos.find(c => c.symbol === symbol);
  if (!crypto) return 'unknown';

  // Check if any network has withdrawal enabled
  const hasWithdrawEnabled = crypto.networks?.some(n => n.withdraw_enabled) || false;
  return hasWithdrawEnabled ? 'enabled' : 'disabled';
}

/**
 * Check deposit status for a specific cryptocurrency on an exchange
 */
export function getDepositStatus(
  symbol: string,
  exchange: number,
  networkStatus: NetworkStatusData
): TransferStatus {
  const exchangeStatus = networkStatus[exchange];
  if (!exchangeStatus) return 'unknown';

  // Check if cryptos is an array
  if (!Array.isArray(exchangeStatus.cryptos)) {
    console.warn(`Exchange ${exchange} cryptos is not an array:`, exchangeStatus.cryptos);
    return 'unknown';
  }

  const crypto = exchangeStatus.cryptos.find(c => c.symbol === symbol);
  if (!crypto) return 'unknown';

  // Check if any network has deposit enabled
  const hasDepositEnabled = crypto.networks?.some(n => n.deposit_enabled) || false;
  return hasDepositEnabled ? 'enabled' : 'disabled';
}

/**
 * Get transfer status for arbitrage opportunity
 * Checks if we can withdraw from buy exchange and deposit to sell exchange
 * with matching network types
 */
export function getArbitrageTransferStatus(
  opportunity: ArbitrageOpportunity,
  networkStatus: NetworkStatusData
): { withdrawStatus: TransferStatus; depositStatus: TransferStatus } {
  const baseCurrency = opportunity.base;
  const buyExchange = opportunity.buy_exchange;
  const sellExchange = opportunity.sell_exchange;

  const buyStatus = networkStatus[buyExchange];
  const sellStatus = networkStatus[sellExchange];

  // If neither has data, return unknown (not assumed transferrable)
  if (!buyStatus && !sellStatus) {
    return { withdrawStatus: 'unknown', depositStatus: 'unknown' };
  }

  // Special case: If one exchange has data and the other doesn't
  // If buy exchange has data and withdraw is enabled, mark withdraw as per data but deposit as unknown
  if (buyStatus && !sellStatus) {
    const withdrawStatus = getWithdrawStatus(baseCurrency, buyExchange, networkStatus);
    // Don't assume deposit is enabled just because withdraw is - mark as unknown
    return { withdrawStatus, depositStatus: 'unknown' };
  }

  // If sell exchange has data and deposit is enabled, mark deposit as per data but withdraw as unknown
  if (!buyStatus && sellStatus) {
    const depositStatus = getDepositStatus(baseCurrency, sellExchange, networkStatus);
    // Don't assume withdraw is enabled just because deposit is - mark as unknown
    return { withdrawStatus: 'unknown', depositStatus };
  }

  // Both exchanges have data - check for matching networks
  const buyCrypto = Array.isArray(buyStatus.cryptos) 
    ? buyStatus.cryptos.find(c => c.symbol === baseCurrency)
    : undefined;
  const sellCrypto = Array.isArray(sellStatus.cryptos)
    ? sellStatus.cryptos.find(c => c.symbol === baseCurrency)
    : undefined;

  if (!buyCrypto || !sellCrypto) {
    return { withdrawStatus: 'unknown', depositStatus: 'unknown' };
  }

  // Find common networks where withdrawal and deposit are both enabled
  let canWithdraw = false;
  let canDeposit = false;
  let hasCommonNetwork = false;

  for (const buyNetwork of buyCrypto.networks) {
    if (buyNetwork.withdraw_enabled) {
      canWithdraw = true;
      
      // Find matching network on sell exchange
      for (const sellNetwork of sellCrypto.networks) {
        if (sellNetwork.deposit_enabled && isSameNetwork(buyNetwork.network, sellNetwork.network)) {
          canDeposit = true;
          hasCommonNetwork = true;
          break;
        }
      }
    }
  }

  // Determine final status based on network compatibility
  if (hasCommonNetwork) {
    return { withdrawStatus: 'enabled', depositStatus: 'enabled' };
  } else if (canWithdraw && !canDeposit) {
    // Can withdraw but no matching deposit network
    return { withdrawStatus: 'enabled', depositStatus: 'disabled' };
  } else if (!canWithdraw && canDeposit) {
    // Can deposit but no matching withdrawal network
    return { withdrawStatus: 'disabled', depositStatus: 'enabled' };
  } else if (canWithdraw || canDeposit) {
    // Has networks but they don't match
    return { withdrawStatus: canWithdraw ? 'enabled' : 'disabled', depositStatus: canDeposit ? 'enabled' : 'disabled' };
  } else {
    return { withdrawStatus: 'disabled', depositStatus: 'disabled' };
  }
}

/**
 * Get network details for a specific cryptocurrency
 */
export function getCryptoNetworkDetails(
  symbol: string,
  exchange: number,
  networkStatus: NetworkStatusData
): Crypto | null {
  const exchangeStatus = networkStatus[exchange];
  if (!exchangeStatus) return null;

  // Check if exchangeStatus is valid and has cryptos property
  if (!exchangeStatus.cryptos || !Array.isArray(exchangeStatus.cryptos)) {
    // Only warn once per exchange to avoid console spam
    if (typeof exchangeStatus === 'object' && Object.keys(exchangeStatus).length === 0) {
      // Empty object - likely data hasn't loaded yet
      return null;
    }
    console.warn(`Exchange ${exchange} has invalid network status data:`, exchangeStatus);
    return null;
  }

  return exchangeStatus.cryptos.find(c => c.symbol === symbol) || null;
}

/**
 * Get exchange deposit/withdrawal URL for manual checking
 */
export function getExchangeNetworkStatusUrl(exchange: number): string {
  const urls: { [key: number]: string } = {
    0: 'https://upbit.com/service_center/guide', // Upbit
    1: 'https://www.bithumb.com/react/customer-support/notice', // Bithumb
    2: 'https://coinone.co.kr/support/notice', // Coinone
    3: 'https://www.binance.com/en/network', // Binance
    4: 'https://www.htx.com/support/en-us/detail/360000193772', // HTX
    5: 'https://www.gate.io/page/deposit_withdraw_status', // Gate.io
    6: 'https://www.kucoin.com/news/categories/listing', // KuCoin
    7: 'https://www.okx.com/support-center', // OKX
    8: 'https://www.mexc.com/support', // MEXC
    9: 'https://www.bybit.com/en-US/help-center', // Bybit
    10: 'https://www.bitget.com/support', // Bitget
  };
  
  return urls[exchange] || '#';
}